<template>
  <UiFormAsyncSelect
    :id="id"
    v-model="model"
    :mode="mode"
    :placeholder="placeholder"
    :searchable="true"
    :caching="caching"
    :filter-results="true"
    :min-chars="0"
    :delay="300"
    :async-source="contentTypesSource"
    label="name"
    value-prop="id"
    v-bind="$attrs">
    <!-- Custom option display with content type name, model, and optionally account -->
    <template #option="{ option }">
      <div class="flex flex-col">
        <div class="flex items-center">
          <span class="font-medium">{{ option.name }}</span>
          <span
            v-if="option.locationBased"
            class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
              bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <i class="fas fa-map-marker-alt mr-1" />
            Location
          </span>
        </div>
        <div v-if="option.model || (showAccountName && option.accountName)" class="flex items-center gap-2">
          <span v-if="option.model" class="text-xs text-gray-500">{{ option.model }}</span>
          <span v-if="showAccountName && option.accountName" class="text-xs text-gray-500">
            ({{ option.accountName }})
          </span>
        </div>
      </div>
    </template>

    <!-- Custom single value display -->
    <template #singleLabel="{ value }">
      <div class="flex items-center">
        <span class="font-medium">{{ value.name }}</span>
        <span
          v-if="value.locationBased"
          class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
            bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          <i class="fas fa-map-marker-alt mr-1" />
          Location
        </span>
        <span v-if="showAccountName && value.accountName" class="text-gray-500 text-xs ml-2">
          ({{ value.accountName }})
        </span>
      </div>
    </template>

    <template #nooptions>
      <div class="text-gray-500 text-sm py-2">
        No content types found
      </div>
    </template>

    <!-- Pass through other slots -->
    <template v-for="(_, slotName) in $slots" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData" />
    </template>
  </UiFormAsyncSelect>
</template>

<script setup>
import { computed } from 'vue'
import { UiFormAsyncSelect } from '@ui/form/async_select'

const props = defineProps({
  modelValue: {
    type: [String, Array, Number, Object],
    default: null
  },
  id: {
    type: String,
    default: 'content-type-select'
  },
  mode: {
    type: String,
    default: 'single' // single or tags
  },
  placeholder: {
    type: String,
    default: 'Select Content Type'
  },
  // API configuration
  apiFunction: {
    type: Function,
    required: true
  },
  showAccountName: {
    type: Boolean,
    default: false
  },
  caching: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

const model = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const contentTypesSource = async (search) => {
  search = search?.trim() || ''
  try {
    const { data } = await props.apiFunction({ search: search })
    return (data ?? [])
  } catch (error) {
    console.error('Failed to load content types:', error)
    throw error
  }
}

</script>
